import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import SubmitButton from 'components/common/formikForm/SubmitButton';

import { downloadManagementReport } from 'api/rest/analytics';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';

interface ManagementReportDownloadProps {
    from?: string;
    to?: string;
    hasValidDateRange: boolean;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message) {
        return error.message;
    }

    if (typeof error === 'object' && error !== null && 'response' in error) {
        const response = (error as { response?: { data?: unknown } }).response;
        if (typeof response?.data === 'object' && response.data !== null) {
            const message = (response.data as { message?: unknown }).message;
            if (typeof message === 'string' && message) {
                return message;
            }
        }
    }

    return fallback;
};

const getFilenameFromContentDisposition = (contentDisposition?: string): string => {
    if (!contentDisposition) return '';

    const encodedFilename = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    if (encodedFilename) {
        try {
            return decodeURIComponent(encodedFilename);
        } catch {
            return encodedFilename;
        }
    }

    return contentDisposition.match(/filename="?([^";]+)"?/i)?.[1]?.trim() || '';
};

const savePdfBlob = (blob: Blob, filename: string): void => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const ManagementReportDownload: React.FC<ManagementReportDownloadProps> = ({
    from,
    to,
    hasValidDateRange,
}) => {
    const { translate } = useTranslate();
    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState(false);

    const handleDownload = async (): Promise<void> => {
        if (!from || !to || !hasValidDateRange) return;

        setLoading(true);

        try {
            const response = await downloadManagementReport(from, to);
            const contentDisposition = (response.headers['content-disposition'] ??
                response.headers['Content-Disposition']) as string | undefined;
            const filename =
                getFilenameFromContentDisposition(contentDisposition) ||
                `management-report-${from}-${to}.pdf`;
            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], { type: 'application/pdf' });

            savePdfBlob(blob, filename);
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('managementReportDownloadFailed')),
                    type: 'error',
                })
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <SubmitButton
            type="button"
            loading={loading}
            disabled={!hasValidDateRange || !from || !to}
            onClick={() => void handleDownload()}
            aria-label={translate('downloadManagementReport')}
            aria-busy={loading}
            fullWidth
            size="small"
            sx={{
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 40,
                minWidth: 0,
                width: '100%',
                px: 1.5,
                fontSize: 13,
                whiteSpace: 'nowrap',
            }}
        >
            {loading ? 'generatingManagementReport' : 'downloadManagementReport'}
        </SubmitButton>
    );
};

export default ManagementReportDownload;
